import {
  IonAvatar,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonImg,
  IonModal,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
} from "@ionic/react";

import "./Inspection.css";
import Breadcrumb from "../../components/Breadcrumb";
import TractorSvg from "../../assets/svg/tractorLeft.svg";
import ListItemDamage from "../../components/ListItemDamage";
import { addCircleOutline, arrowForwardOutline, close } from "ionicons/icons";
import DamageDefault from "../../assets/image/damageDefault.png";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import ViewDamageModal from "../../components/ViewDamageModal";
import InspectionFooter from "../../components/InspectionFooter";
import ReportDamageModal from "../../components/ReportDamageModal/ReportDamageModal";
import VehicleWithDamages from "../../components/VehicleWithDamages";
import { inspectionRouteMapping } from "../../data/InspectionRoute";
import { Preferences } from "@capacitor/preferences";
import { useGetCurrentJourneyQuery } from "../../api/journey";
import { getDamageTypeById, useGetDamagesQuery } from "../../api/damage";
import { useCookieQuery } from "../../utils/query";
import { Damage } from "../../types/api/damage.types";
import { useGetVehicleByIdQuery } from "../../api/vehicles";
import SecurityModal from "../../components/SecurityModal";

type DamageOptions = {
  id: string;
  number: number;
  type: string;
  label: string;
  damageId?: string;
  comment?: string;
  picture?: string;
}[];

const Inspection: React.FC = () => {
  const { id, side, type } = useParams<{
    id: string;
    side: string;
    type: string;
  }>();

  const { data: vehicle } = useGetVehicleByIdQuery(id);

  const { data: cookieDamages } = useCookieQuery("damages");

  const { data: damages } = useGetDamagesQuery({ vehicleId: id });

  const [damageOptions, setDamageOptions] = useState<DamageOptions | null>();

  let currentPartDamages: DamageOptions =
    damages
      ?.filter(
        (el) =>
          el.type.vehiclePart.type.code === type &&
          el.type.vehiclePart.vehicleSide.code === side
      )
      ?.map((el, index) => {
        return {
          id: el.type.vehiclePart.code,
          number: index + 1,
          type: el.type.title,
          label: el.type.vehiclePart.title,
          damageId: el.id,
          picture: el.picture?.id,
          comment: el.comment,
        };
      }) || [];

  const [selectSide, setSelectSide] = useState(side);
  const [selectType, setSelectType] = useState(type);

  const inspectionState = inspectionRouteMapping(id)[type][side];

  const [damageModalIsOpen, setDamageModalIsOpen] = useState(false);
  const [reportDamageModalIsOpen, setReportDamageModalIsOpen] = useState(false);
  const [securityModalIsOpen, setSecurityModalIsOpen] = useState(false);
  const [step, setStep] = useState(
    side === "Inside" || side === "TrailerInside" ? 1 : 0
  );
  const [selectedDamage, setSelectedDamage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      let cookies = JSON.parse(
        (await Preferences.get({ key: "damages" })).value as string
      );
      if (!cookies) return null;
      let data = await Promise.all(
        cookies?.map(async (el: Damage) => {
          if (!el.damageTypeId) return null;
          const result = await getDamageTypeById(el.damageTypeId);
          if (!result) return null;
          return { ...result, ...el };
        })
      );

      data = data
        ?.filter(
          (el) =>
            el.vehiclePart?.type?.code === type &&
            el.vehiclePart?.vehicleSide?.code === side
        )
        ?.map((el, index) => {
          return {
            id: el.vehiclePart.code,
            number: currentPartDamages.length + index + 1,
            type: el.title,
            label: el.vehiclePart.title,
            damageId: null,
            picture: el.picture,
            comment: el.comment,
          };
        });

      setDamageOptions(data);
    };

    fetchData();
  }, [cookieDamages, side, type, reportDamageModalIsOpen, damages]);

  useEffect(() => {
    setSelectSide(side);
    setSelectType(type);
    setStep(side === "Inside" || side === "TrailerInside" ? 1 : 0);
  }, [side, type]);

  const DocumentList = {
    Carrier: [
      "Carte grise",
      "License communautaire",
      "Contrat de location",
      "Constat amiable",
      "Fiche info assurance",
    ],
    Tractor: [
      "Carte grise",
      "License communautaire",
      "Contrat de location",
      "Constat amiable",
      "Fiche info assurance",
    ],
    Trailer: ["Carte grise", "Contrat de location", "Fiche info assurance"],
  };

  return (
    <IonPage>
      <IonContent fullscreen className="Inspection">
        <SecurityModal
          isOpen={securityModalIsOpen}
          setOpen={setSecurityModalIsOpen}
          nextUrl={inspectionState.nextUrl}
        />
        {selectedDamage ? (
          <ViewDamageModal
            damageModalIsOpen={damageModalIsOpen}
            setDamageModalIsOpen={setDamageModalIsOpen}
            damageId={
              currentPartDamages?.findLast(
                (el) => el.damageId === selectedDamage
              )?.damageId ||
              damageOptions?.findLast(
                (el) => `${el.number}-${el.damageId}` === selectedDamage
              )?.damageId ||
              ""
            }
            damageType={
              damages?.findLast((el) => el.id === selectedDamage)?.type.title ||
              damageOptions?.findLast(
                (el) => `${el.number}-${el.damageId}` === selectedDamage
              )?.type ||
              ""
            }
            vehiclePart={
              damages?.findLast((el) => el.id === selectedDamage)?.type
                .vehiclePart.title ||
              damageOptions?.findLast(
                (el) => `${el.number}-${el.damageId}` === selectedDamage
              )?.label ||
              ""
            }
            damagePicture={
              damages?.findLast((el) => el.id === selectedDamage)?.picture
                ?.id ||
              damageOptions?.findLast(
                (el) => `${el.number}-${el.damageId}` === selectedDamage
              )?.picture ||
              ""
            }
            comment={
              damages?.findLast((el) => el.id === selectedDamage)?.comment ||
              damageOptions?.findLast(
                (el) => `${el.number}-${el.damageId}` === selectedDamage
              )?.comment ||
              ""
            }
          />
        ) : null}
        <ReportDamageModal
          isOpen={reportDamageModalIsOpen}
          setOpen={setReportDamageModalIsOpen}
          selectSide={selectSide}
          setSelectSide={setSelectSide}
          setSelectType={setSelectType}
          selectType={selectType}
          step={step}
          setStep={setStep}
          vehicleId={id}
        />

        <Breadcrumb
          backUrl={inspectionState.prevUrl}
          activeElement={inspectionState.breadcrumbState}
          trailer={vehicle?.type?.code !== "Carrier"}
          backConfirmation={
            inspectionState.breadcrumbState === 1.1 ||
            inspectionState.breadcrumbState === 3.1
          }
        />
        <IonRow className="Inspection-name">
          Inspection {vehicle?.type?.label} {vehicle?.registrationNumber}{" "}
          {vehicle?.internalCode}
        </IonRow>
        <IonRow className="Inspection-title">{inspectionState.name}</IonRow>
        {side === "Document" ? (
          <div className="Inspection-description">
            {inspectionState.description}{" "}
            <span className="Inspection-description_highlight">
              vérifiez la présence et l'état des document suivants :{" "}
            </span>
          </div>
        ) : (
          <div className="Inspection-description">
            {inspectionState.description}{" "}
            <span className="Inspection-description_highlight">
              signalez un nouveau dommage{" "}
            </span>{" "}
            si nécessaire
          </div>
        )}

        <IonRow>
          {side === "Document" ? (
            <div className="Inspection-documents">
              <ul className="Inspection-documents-list">
                {DocumentList[type as "Carrier" | "Trailer" | "Tractor"].map(
                  (el) => (
                    <li className="Inspection-document-list-item">{el}</li>
                  )
                )}
              </ul>
              {currentPartDamages.length > 0 ||
              (damageOptions && damageOptions?.length > 0) ? (
                <div className="Inspection-documents-warning">!</div>
              ) : null}
            </div>
          ) : (
            <VehicleWithDamages
              elementSvg={inspectionState.svg}
              damageList={[
                ...(currentPartDamages || []),
                ...(damageOptions || []),
              ]}
              setDamageModalIsOpen={setDamageModalIsOpen}
              setSelectedDamage={setSelectedDamage}
            />
          )}
        </IonRow>
        <InspectionFooter
          nextUrl={inspectionState.nextUrl}
          setDamageModalIsOpen={setDamageModalIsOpen}
          setReportDamageModal={setReportDamageModalIsOpen}
          damageList={[...(currentPartDamages || []), ...(damageOptions || [])]}
          setSelectedDamage={setSelectedDamage}
          side={side}
          securityCheck={inspectionState.securityCheck}
          setSecurityModalIsOpen={setSecurityModalIsOpen}
        />
      </IonContent>
    </IonPage>
  );
};

export default Inspection;
